import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import StatCard from "@/components/StatCard";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { DollarSign, TrendingUp, MousePointerClick, Target, Plus, Facebook, Chrome, MapPin } from "lucide-react";
import { toast } from "sonner";

const PLATFORM = { meta: { label: "Meta / Facebook", color: "#1877F2", icon: Facebook }, google: { label: "Google PPC", color: "#EA4335", icon: Chrome }, local: { label: "Local Services Ads", color: "#34A853", icon: MapPin } };

export default function Marketing() {
  const { user } = useAuth();
  const isStaff = user.role !== "client";
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(isStaff ? "" : user.client_id);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ platform: "meta", date: new Date().toISOString().slice(0, 7), spend: 0, impressions: 0, clicks: 0, conversions: 0, revenue: 0 });

  useEffect(() => {
    if (isStaff) api.get("/clients").then((r) => { setClients(r.data); if (r.data[0]) setClientId(r.data[0].id); });
  }, [isStaff]);
  const load = useCallback((cid) => { if (cid) api.get(`/clients/${cid}/marketing`).then((r) => setData(r.data)); }, []);
  useEffect(() => { load(clientId); }, [clientId, load]);

  const save = async () => {
    try {
      await api.post(`/clients/${clientId}/marketing`, {
        ...form, spend: +form.spend, impressions: +form.impressions, clicks: +form.clicks,
        conversions: +form.conversions, revenue: +form.revenue,
      });
      toast.success("Saved");
      setOpen(false);
      load(clientId);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const t = data?.totals || {};
  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Marketing & Ads</h1>
          <p className="text-slate-500 text-sm mt-1">Facebook / Meta & Google PPC performance</p>
        </div>
        <div className="flex items-center gap-2">
          {isStaff && (
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="w-48" data-testid="marketing-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {isStaff && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button className="text-white" style={{ background: "var(--tenant-primary)" }} data-testid="add-marketing-btn"><Plus size={16} className="mr-1" /> Add data</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-display">Add ad performance</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs uppercase tracking-wider text-slate-500">Platform</Label>
                      <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                        <SelectTrigger className="mt-1.5" data-testid="mk-platform-select"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="meta">Meta / Facebook</SelectItem><SelectItem value="google">Google PPC</SelectItem><SelectItem value="local">Local Services Ads</SelectItem></SelectContent>
                      </Select></div>
                    <div><Label className="text-xs uppercase tracking-wider text-slate-500">Month</Label><Input type="month" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {["spend", "impressions", "clicks", "conversions", "revenue"].map((k) => (
                      <div key={k}><Label className="text-[10px] uppercase tracking-wider text-slate-500">{k}</Label>
                        <Input type="number" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5" data-testid={`mk-${k}`} /></div>
                    ))}
                  </div>
                </div>
                <DialogFooter><Button onClick={save} className="text-white" style={{ background: "var(--tenant-primary)" }} data-testid="save-marketing-btn">Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {!data || !data.series.length ? (
        <Card className="p-12 text-center border-slate-200 text-slate-400 text-sm">No ad data yet for this client.</Card>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            <StatCard testid="mk-spend" label="Ad Spend" value={`$${(t.spend || 0).toLocaleString()}`} icon={DollarSign} accent="#EF4444" />
            <StatCard testid="mk-revenue" label="Attributed Revenue" value={`$${(t.revenue || 0).toLocaleString()}`} icon={TrendingUp} accent="#10B981" />
            <StatCard testid="mk-roas" label="ROAS" value={`${t.roas || 0}x`} icon={Target} accent="var(--tenant-primary)" sub={`CPA $${t.cpa || 0}`} />
            <StatCard testid="mk-conv" label="Conversions" value={(t.conversions || 0).toLocaleString()} icon={MousePointerClick} accent="#8B5CF6" sub={`${(t.clicks || 0).toLocaleString()} clicks`} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="p-5 border-slate-200 lg:col-span-2">
              <p className="font-display font-semibold text-slate-900 mb-4">Ad Spend by Month</p>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={data.series}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <Tooltip /><Legend />
                  <Area type="monotone" dataKey="meta" stackId="1" name="Meta" stroke="#1877F2" fill="#1877F2" fillOpacity={0.25} />
                  <Area type="monotone" dataKey="google" stackId="1" name="Google" stroke="#EA4335" fill="#EA4335" fillOpacity={0.25} />
                  <Area type="monotone" dataKey="local" stackId="1" name="Local" stroke="#34A853" fill="#34A853" fillOpacity={0.25} />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-5 border-slate-200">
              <p className="font-display font-semibold text-slate-900 mb-4">By Platform</p>
              <div className="space-y-4">
                {data.by_platform.map((p) => {
                  const M = PLATFORM[p.platform] || { label: p.platform, color: "#64748b", icon: Target };
                  const roas = p.spend ? (p.revenue / p.spend).toFixed(1) : 0;
                  return (
                    <div key={p.platform} className="rounded-md border border-slate-200 p-3" data-testid="platform-card">
                      <div className="flex items-center gap-2 mb-2">
                        <M.icon size={16} style={{ color: M.color }} />
                        <span className="text-sm font-medium text-slate-800">{M.label}</span>
                        <span className="ml-auto text-xs px-2 py-0.5 rounded" style={{ background: `${M.color}1a`, color: M.color }}>{roas}x ROAS</span>
                      </div>
                      <div className="grid grid-cols-2 gap-y-1 text-xs text-slate-500">
                        <span>Spend: <b className="text-slate-700">${p.spend.toLocaleString()}</b></span>
                        <span>Rev: <b className="text-slate-700">${p.revenue.toLocaleString()}</b></span>
                        <span>Clicks: <b className="text-slate-700">{p.clicks.toLocaleString()}</b></span>
                        <span>Conv: <b className="text-slate-700">{p.conversions}</b></span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
